"use client";

import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ArrowRight, MessageCircle, Clock, CircleCheck as CheckCircle2, CircleAlert as AlertCircle, Loader as Loader2 } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
interface FormDefinition {
  id?: number | string;
  key?: string;
  name?: string;
  spam?: {
    token?: string;
    honeypot?: {
      field: string;
    } | null;
    min_seconds?: number;
  };
  fields?: Array<{
    id: string;
    key: string;
    label?: string;
    type?: string;
    required?: boolean;
    options?: Array<{
      value: string;
      label: string;
    }>;
    validation?: {
      messages?: {
        required?: string;
        invalid?: string;
      };
    };
  }>;
}
export default function DriverHero() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  const durations = [{
    hours: "2 Stunden"
  }, {
    hours: "4 Stunden"
  }, {
    hours: "6 Stunden"
  }, {
    hours: "8 Stunden"
  }];

  // Wizard reference
  const wizardRef = useRef<HTMLDivElement>(null);

  // Wizard state replicated from BookingRequestForm
  const [formDef, setFormDef] = useState<FormDefinition | null>(null);
  const [isLoadingDef, setIsLoadingDef] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Wizard step state (1 to 4)
  const [currentStep, setCurrentStep] = useState(1);

  // Form input states
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [preferredDate, setPreferredDate] = useState("");
  const [pickupLocation, setPickupLocation] = useState("");
  const [duration, setDuration] = useState("");
  const [destinations, setDestinations] = useState("");
  const [passengers, setPassengers] = useState("");
  const [suitcases, setSuitcases] = useState("");
  const [dropoffLocation, setDropoffLocation] = useState("");
  const [additionalMessage, setAdditionalMessage] = useState("");

  // Honeypot & spam protection
  const [hpValue, setHpValue] = useState("");
  const fetchTimeRef = useRef<number>(Date.now());

  // Submission states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, {
    message?: string;
  }>>({});
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [confirmationMessage, setConfirmationMessage] = useState<string | null>(null);
  useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        const preselected = sessionStorage.getItem("phd_preselected_duration");
        if (preselected) {
          // Normalize if passed in English
          const normalized = preselected.replace("Hours", "Stunden").replace("Hour", "Stunde");
          setDuration(normalized);
          sessionStorage.removeItem("phd_preselected_duration");
        }
      } catch {
        // Ignore storage access errors
      }
      const handlePreselectEvent = (e: Event) => {
        const customEvent = e as CustomEvent<string>;
        if (customEvent.detail) {
          const normalized = customEvent.detail.replace("Hours", "Stunden").replace("Hour", "Stunde");
          setDuration(normalized);
        }
      };
      window.addEventListener("phd-preselect-duration", handlePreselectEvent);
      return () => {
        window.removeEventListener("phd-preselect-duration", handlePreselectEvent);
      };
    }
  }, []);
  useEffect(() => {
    let isCancelled = false;
    const client = getWvcClient();
    if (!client?.pluginRequest) {
      setIsLoadingDef(false);
      return;
    }
    client.pluginRequest("wvc-forms/v1/forms/private_driver_hire_booking_de").then((res: FormDefinition) => {
      if (!isCancelled) {
        setFormDef(res);
        fetchTimeRef.current = Date.now();
        setIsLoadingDef(false);
      }
    }).catch(() => {
      client.pluginRequest("wvc-forms/v1/forms/private_driver_hire_booking_de/schema").then((schemaRes: FormDefinition) => {
        if (!isCancelled) {
          setFormDef(schemaRes);
          fetchTimeRef.current = Date.now();
          setIsLoadingDef(false);
        }
      }).catch(() => {
        if (!isCancelled) {
          setLoadError("Die Buchungskonfiguration konnte derzeit nicht geladen werden.");
          setIsLoadingDef(false);
        }
      });
    });
    return () => {
      isCancelled = true;
    };
  }, []);
  const handleQuickDurationClick = (hours: string) => {
    setDuration(hours);
    setFieldErrors(prev => {
      const updated = {
        ...prev
      };
      delete updated.duration;
      return updated;
    });
  };
  const validateStep = (step: number): boolean => {
    const stepErrors: Record<string, {
      message: string;
    }> = {};
    if (step === 1) {
      if (!fullName.trim()) stepErrors.fullName = {
        message: "Bitte geben Sie Ihren vollständigen Namen ein."
      };
      if (!phone.trim()) stepErrors.phone = {
        message: "Bitte geben Sie Ihre WhatsApp- oder Telefonnummer an."
      };
      if (!email.trim() || !email.includes("@")) stepErrors.email = {
        message: "Bitte geben Sie eine gültige E-Mail-Adresse ein."
      };
    } else if (step === 2) {
      if (!preferredDate) stepErrors.preferredDate = {
        message: "Bitte wählen Sie Ihr Wunschdatum."
      };
      if (!pickupLocation.trim()) stepErrors.pickupLocation = {
        message: "Bitte geben Sie das Abholhotel oder den Abholort an."
      };
      if (!duration) stepErrors.duration = {
        message: "Bitte wählen Sie die Dauer aus."
      };
    } else if (step === 3) {
      if (!destinations.trim()) stepErrors.destinations = {
        message: "Bitte geben Sie geplante Ziele oder Stopps an."
      };
      if (!passengers || Number(passengers) < 1) stepErrors.passengers = {
        message: "Bitte mindestens 1 Passagier angeben."
      };
      if (suitcases === "" || Number(suitcases) < 0) stepErrors.suitcases = {
        message: "Bitte Kofferanzahl angeben."
      };
      if (!dropoffLocation.trim()) stepErrors.dropoffLocation = {
        message: "Bitte den Zielort angeben."
      };
    }
    if (Object.keys(stepErrors).length > 0) {
      setFieldErrors(prev => ({
        ...prev,
        ...stepErrors
      }));
      return false;
    }
    setFieldErrors(prev => {
      const updated = {
        ...prev
      };
      if (step === 1) {
        delete updated.fullName;
        delete updated.phone;
        delete updated.email;
      } else if (step === 2) {
        delete updated.preferredDate;
        delete updated.pickupLocation;
        delete updated.duration;
      } else if (step === 3) {
        delete updated.destinations;
        delete updated.passengers;
        delete updated.suitcases;
        delete updated.dropoffLocation;
      }
      return updated;
    });
    return true;
  };
  const handleNext = () => {
    setGeneralError(null);
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };
  const handleBack = () => {
    setGeneralError(null);
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setGeneralError(null);
    setFieldErrors({});
    const newErrors: Record<string, {
      message: string;
    }> = {};
    if (!fullName.trim()) newErrors.fullName = {
      message: "Bitte geben Sie Ihren vollständigen Namen ein."
    };
    if (!phone.trim()) newErrors.phone = {
      message: "Bitte geben Sie Ihre WhatsApp- oder Telefonnummer an."
    };
    if (!email.trim() || !email.includes("@")) newErrors.email = {
      message: "Bitte geben Sie eine gültige E-Mail-Adresse ein."
    };
    if (!preferredDate) newErrors.preferredDate = {
      message: "Bitte wählen Sie Ihr Wunschdatum."
    };
    if (!pickupLocation.trim()) newErrors.pickupLocation = {
      message: "Bitte geben Sie das Abholhotel oder den Abholort an."
    };
    if (!duration) newErrors.duration = {
      message: "Bitte wählen Sie die Dauer aus."
    };
    if (!destinations.trim()) newErrors.destinations = {
      message: "Bitte geben Sie geplante Ziele oder Stopps an."
    };
    if (!passengers || Number(passengers) < 1) newErrors.passengers = {
      message: "Bitte mindestens 1 Passagier angeben."
    };
    if (suitcases === "" || Number(suitcases) < 0) newErrors.suitcases = {
      message: "Bitte Kofferanzahl angeben."
    };
    if (!dropoffLocation.trim()) newErrors.dropoffLocation = {
      message: "Bitte den Zielort angeben."
    };
    if (Object.keys(newErrors).length > 0) {
      setFieldErrors(newErrors);
      if (newErrors.fullName || newErrors.phone || newErrors.email) {
        setCurrentStep(1);
      } else if (newErrors.preferredDate || newErrors.pickupLocation || newErrors.duration) {
        setCurrentStep(2);
      } else if (newErrors.destinations || newErrors.passengers || newErrors.suitcases || newErrors.dropoffLocation) {
        setCurrentStep(3);
      }
      return;
    }
    const minSeconds = formDef?.spam?.min_seconds ?? 2;
    const elapsedSeconds = (Date.now() - fetchTimeRef.current) / 1000;
    if (elapsedSeconds < minSeconds) {
      await new Promise(resolve => setTimeout(resolve, (minSeconds - elapsedSeconds + 0.5) * 1000));
    }
    setIsSubmitting(true);
    const client = getWvcClient();
    const token = formDef?.spam?.token || "standard_token";
    const hpField = formDef?.spam?.honeypot?.field || "frms_hp_booking";
    const durationMap: Record<string, string> = {
      "2 Stunden": "2_hours",
      "4 Stunden": "4_hours",
      "6 Stunden": "6_hours",
      "8 Stunden": "8_hours"
    };
    const mappedDuration = durationMap[duration] || duration;
    const valuesPayload: Record<string, any> = {
      full_name: fullName,
      whatsapp_number: phone,
      email_address: email,
      preferred_date: preferredDate,
      pickup_location: pickupLocation,
      duration: mappedDuration,
      destinations_stops: destinations,
      number_of_passengers: passengers,
      number_of_suitcases: suitcases,
      dropoff_location: dropoffLocation,
      additional_message: additionalMessage
    };
    if (formDef?.fields && Array.isArray(formDef.fields)) {
      formDef.fields.forEach(f => {
        if (f.key && valuesPayload[f.key] !== undefined) {
          valuesPayload[f.id] = valuesPayload[f.key];
        }
      });
    }
    const payload = {
      values: valuesPayload,
      token,
      hp: {
        [hpField]: hpValue
      },
      meta: {
        source_url: typeof window !== "undefined" ? window.location.href : "",
        referrer: typeof document !== "undefined" ? document.referrer : ""
      }
    };
    try {
      if (client?.pluginRequest) {
        let res: any;
        try {
          res = await client.pluginRequest("wvc-forms/v1/forms/private_driver_hire_booking_de/submit", {
            method: "POST",
            data: payload
          });
        } catch {
          res = await client.pluginRequest("wvc-forms/v1/forms/private_driver_hire_booking_de/entries", {
            method: "POST",
            data: payload
          });
        }
        if (res?.success) {
          if (res?.confirmation?.message) {
            setConfirmationMessage(res.confirmation.message);
          } else {
            setConfirmationMessage("Vielen Dank! Ihre Buchungsanfrage für einen Privatfahrer ist eingegangen. Unser Team wird Ihre Route prüfen und sich in Kürze bei Ihnen melden.");
          }
        } else if (res?.code === "frms_validation_failed" && res?.errors) {
          setFieldErrors(res.errors);
          setGeneralError(res.message || "Bitte überprüfen Sie die markierten Felder.");
        } else {
          setGeneralError(res?.message || "Ihre Anfrage konnte nicht gesendet werden. Bitte versuchen Sie es erneut oder kontaktieren Sie uns direkt.");
        }
      } else {
        setGeneralError("Der Buchungsservice ist derzeit nicht erreichbar. Bitte kontaktieren Sie uns direkt per WhatsApp (+20 101 063 4627), um Ihre Anfrage abzuschließen.");
      }
    } catch (err: any) {
      setGeneralError(err?.message || "Beim Senden Ihrer Buchung ist ein Netzwerkfehler aufgetreten. Bitte versuchen Sie es erneut.");
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section id="privatecardriverhero" data-nav="light" className="relative bg-[#E8F2F1] text-[#333333] py-16 md:py-24 lg:py-28">
      <div className="max-w-[1136px] mx-auto px-6">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Left Column: Heading, Copy, and CTAs */}
          <div className="lg:col-span-6 space-y-6">
            <motion.div variants={itemVariants} className="inline-flex items-center gap-2">
              <span className="w-2 h-2 bg-[#D99000] shrink-0" aria-hidden="true" />
              <span className="text-xs sm:text-sm font-bold tracking-wider uppercase text-[#D99000]">
                STÜNDLICHER PRIVATFAHRER-SERVICE
              </span>
            </motion.div>

            <motion.h1 variants={itemVariants} className="text-4xl sm:text-5xl lg:text-[3.25rem] font-bold tracking-tight text-[#324A6D] leading-[1.14]">
              Ihr Fahrer. Ihr Zeitplan. Ihre Zwischenstopps.
            </motion.h1>

            <motion.p variants={itemVariants} className="text-lg text-muted-foreground leading-relaxed">
              Buchen Sie ein privates Fahrzeug mit lizenziertem Fahrer für 2 bis 8 Stunden. Teilen Sie uns Ihre bevorzugte Route und Zwischenstopps mit — wir bestätigen Reiseroute, Dauer und Preis, bevor Ihre Buchung abgeschlossen wird.
            </motion.p>

            <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-4 pt-2">
              <Button asChild size="lg" className="rounded-lg shadow-sm bg-[#D99000] text-white hover:bg-[#D99000]/90 transition-[color,background-color,border-color,box-shadow]">
                <Link to="https://wa.me/201010634627?text=Hallo%20Hurghada%20Transfer%20Hub,%20ich%20m%C3%B6chte%20ein%20Angebot%20f%C3%BCr%20einen%20Privatfahrer%20anfragen." newTab>
                  <span>Angebot anfordern</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild size="lg" className="rounded-lg shadow-sm bg-[#27BA2A] text-white hover:bg-[#22a025] transition-[color,background-color,border-color,box-shadow]">
                <Link to="https://wa.me/201010634627" newTab>
                  <MessageCircle className="size-4" />
                  <span>Auf WhatsApp fragen</span>
                </Link>
              </Button>
            </motion.div>
          </div>

          {/* Right Column: White Booking-Style Information Card with 4-Step Wizard */}
          <motion.div variants={itemVariants} className="lg:col-span-6">
            <div className="bg-white border border-[#D9E1E2] rounded-2xl shadow-lg p-6 md:p-8 space-y-6">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-[#324A6D]">
                  Wählen Sie Ihre Zeit
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Wählen Sie Ihre geschätzte Dauer, um eine direkte Anfrage zu starten.
                </p>
              </div>

              {/* 2x2 Quick Duration Buttons */}
              <div className="grid grid-cols-2 gap-3">
                {durations.map((item, index) => <Button key={item.hours} data-index={index} type="button" variant="outline" onClick={() => handleQuickDurationClick(item.hours)} className={`h-auto py-3.5 px-4 rounded-lg border font-semibold text-sm transition-[color,background-color,border-color,box-shadow] flex items-center justify-center gap-2 cursor-pointer ${duration === item.hours ? "border-[#D99000] bg-[#E8F2F1]/50 text-[#324A6D]" : "border-[#D9E1E2] bg-white text-[#324A6D] hover:border-[#D99000] hover:bg-[#E8F2F1]/50 hover:text-[#324A6D]"}`}>
                    <Clock className="size-4 text-[#D99000]" />
                    <span>{item.hours}</span>
                  </Button>)}
              </div>

              {/* 4-Step Booking Wizard Form */}
              <div ref={wizardRef} className="border-t border-[#E5E7EB] pt-6">
                {isLoadingDef ? <div className="space-y-6">
                    <Skeleton className="h-10 w-full" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Skeleton className="h-10 w-full" />
                      <Skeleton className="h-10 w-full" />
                    </div>
                    <Skeleton className="h-24 w-full" />
                    <Skeleton className="h-12 w-48 mx-auto" />
                  </div> : confirmationMessage ? <div className="text-center py-6 space-y-4">
                    <div className="inline-flex items-center justify-center size-14 rounded-lg bg-emerald-100 text-emerald-700 mx-auto">
                      <CheckCircle2 className="size-8" />
                    </div>
                    <h3 className="text-xl font-semibold text-[#324A6D]">
                      Buchungsanfrage gesendet
                    </h3>
                    <div className="text-muted-foreground text-sm max-w-md mx-auto leading-relaxed" dangerouslySetInnerHTML={{
                  __html: confirmationMessage
                }} />
                    <div className="pt-4">
                      <Button type="button" variant="outline" onClick={() => {
                    setConfirmationMessage(null);
                    setCurrentStep(1);
                    setFullName("");
                    setPhone("");
                    setEmail("");
                    setPreferredDate("");
                    setPickupLocation("");
                    setDuration("");
                    setDestinations("");
                    setPassengers("");
                    setSuitcases("");
                    setDropoffLocation("");
                    setAdditionalMessage("");
                    setFieldErrors({});
                    setGeneralError(null);
                  }} className="border-[#D9E1E2] text-[#324A6D] hover:bg-[#E8F2F1]/50 rounded-xl px-6">
                        Weitere Anfrage stellen
                      </Button>
                    </div>
                  </div> : <form onSubmit={handleSubmit} noValidate className="space-y-6">
                    {/* Spam Honeypot */}
                    {formDef?.spam?.honeypot && <div style={{
                  position: "absolute",
                  width: "1px",
                  height: "1px",
                  padding: 0,
                  margin: "-1px",
                  overflow: "hidden",
                  clip: "rect(0,0,0,0)",
                  whiteSpace: "nowrap",
                  border: 0
                }} aria-hidden="true">
                        <label htmlFor={formDef.spam.honeypot.field}>Website Decoy</label>
                        <input id={formDef.spam.honeypot.field} name={formDef.spam.honeypot.field} type="text" value={hpValue} tabIndex={-1} autoComplete="off" onChange={e => setHpValue(e.target.value)} />
                      </div>}

                    {/* Progress Indicator */}
                    <div className="mb-6 pb-6 border-b border-[#D9E1E2]">
                      <div className="flex items-center justify-between mb-3 text-xs sm:text-sm">
                        <span className="font-semibold text-[#324A6D]">
                          Schritt {currentStep} von 4:{" "}
                          <span className="text-[#D99000] font-medium">
                            {currentStep === 1 && "Kontaktdaten"}
                            {currentStep === 2 && "Buchungsdetails"}
                            {currentStep === 3 && "Fahrtdetails"}
                            {currentStep === 4 && "Abschlussdetails"}
                          </span>
                        </span>
                        <span className="text-muted-foreground font-medium text-xs">
                          {Math.round(currentStep / 4 * 100)}% abgeschlossen
                        </span>
                      </div>

                      <div className="grid grid-cols-4 gap-2">
                        {[1, 2, 3, 4].map((step, index) => {
                      const isCompleted = currentStep > step;
                      const isActive = currentStep === step;
                      return <div key={step} data-index={index} className="flex flex-col gap-1.5">
                              <div className={`h-2 rounded-lg transition-colors duration-200 ${isActive ? "bg-[#D99000]" : isCompleted ? "bg-[#324A6D]" : "bg-[#D9E1E2]"}`} />
                              <span className={`hidden sm:inline-block text-[11px] font-medium truncate ${isActive ? "text-[#D99000]" : isCompleted ? "text-[#324A6D]" : "text-muted-foreground"}`}>
                                {step === 1 && "Kontakt"}
                                {step === 2 && "Buchung"}
                                {step === 3 && "Fahrt"}
                                {step === 4 && "Prüfen & Senden"}
                              </span>
                            </div>;
                    })}
                      </div>
                    </div>

                    {loadError && <Alert variant="destructive" className="mb-4">
                        <AlertCircle className="size-4" />
                        <AlertTitle>Hinweis</AlertTitle>
                        <AlertDescription>{loadError}</AlertDescription>
                      </Alert>}

                    {generalError && <Alert variant="destructive" className="mb-4">
                        <AlertCircle className="size-4" />
                        <AlertTitle>Fehler</AlertTitle>
                        <AlertDescription>{generalError}</AlertDescription>
                      </Alert>}

                    {/* STEP 1: Contact Details */}
                    <div className={currentStep === 1 ? "space-y-6" : "hidden"}>
                      {/* Full Name */}
                      <div className="space-y-1.5">
                        <Label htmlFor="full_name" className="text-sm font-medium text-foreground">
                          Vollständiger Name <span className="text-destructive">*</span>
                        </Label>
                        <Input id="full_name" type="text" placeholder="z. B. Max Mustermann" value={fullName} onChange={e => setFullName(e.target.value)} className={`bg-background ${fieldErrors.fullName ? "border-destructive focus-visible:ring-destructive" : ""}`} required />
                        {fieldErrors.fullName && <p className="text-xs text-destructive">{fieldErrors.fullName.message}</p>}
                      </div>

                      {/* WhatsApp / Phone & Email */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label htmlFor="phone_number" className="text-sm font-medium text-foreground">
                            WhatsApp- / Telefonnummer <span className="text-destructive">*</span>
                          </Label>
                          <Input id="phone_number" type="tel" placeholder="+20 100 000 0000" value={phone} onChange={e => setPhone(e.target.value)} className={`bg-background ${fieldErrors.phone ? "border-destructive focus-visible:ring-destructive" : ""}`} required />
                          {fieldErrors.phone && <p className="text-xs text-destructive">{fieldErrors.phone.message}</p>}
                        </div>

                        <div className="space-y-1.5">
                          <Label htmlFor="email" className="text-sm font-medium text-foreground">
                            E-Mail-Adresse <span className="text-destructive">*</span>
                          </Label>
                          <Input id="email" type="email" placeholder="max@beispiel.de" value={email} onChange={e => setEmail(e.target.value)} className={`bg-background ${fieldErrors.email ? "border-destructive focus-visible:ring-destructive" : ""}`} required />
                          {fieldErrors.email && <p className="text-xs text-destructive">{fieldErrors.email.message}</p>}
                        </div>
                      </div>

                      {/* Step 1 Navigation */}
                      <div className="flex items-center justify-end pt-4 border-t border-[#D9E1E2]">
                        <Button type="button" onClick={handleNext} className="bg-[#D99000] text-white hover:bg-[#D99000]/90 rounded-xl px-7 py-2.5 text-sm font-medium w-full sm:w-auto">
                          Weiter
                        </Button>
                      </div>
                    </div>

                    {/* STEP 2: Booking Details */}
                    <div className={currentStep === 2 ? "space-y-6" : "hidden"}>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label htmlFor="preferred_date" className="text-sm font-medium text-foreground">
                            Wunschdatum <span className="text-destructive">*</span>
                          </Label>
                          <Input id="preferred_date" type="date" value={preferredDate} onChange={e => setPreferredDate(e.target.value)} className={`bg-background ${fieldErrors.preferredDate ? "border-destructive focus-visible:ring-destructive" : ""}`} required />
                          {fieldErrors.preferredDate && <p className="text-xs text-destructive">{fieldErrors.preferredDate.message}</p>}
                        </div>

                        <div className="space-y-1.5">
                          <Label htmlFor="pickup_location" className="text-sm font-medium text-foreground">
                            Abholort oder Hotel <span className="text-destructive">*</span>
                          </Label>
                          <Input id="pickup_location" type="text" placeholder="z. B. Steigenberger ALDAU Beach Hotel" value={pickupLocation} onChange={e => setPickupLocation(e.target.value)} className={`bg-background ${fieldErrors.pickupLocation ? "border-destructive focus-visible:ring-destructive" : ""}`} required />
                          {fieldErrors.pickupLocation && <p className="text-xs text-destructive">{fieldErrors.pickupLocation.message}</p>}
                        </div>
                      </div>

                      {/* Duration Dropdown */}
                      <div className="space-y-1.5">
                        <Label htmlFor="duration" className="text-sm font-medium text-foreground">
                          Dauer <span className="text-destructive">*</span>
                        </Label>
                        <Select value={duration} onValueChange={setDuration}>
                          <SelectTrigger id="duration" className={`w-full bg-background ${fieldErrors.duration ? "border-destructive" : ""}`}>
                            <SelectValue placeholder="Dauer auswählen" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="2 Stunden">2 Stunden</SelectItem>
                            <SelectItem value="4 Stunden">4 Stunden</SelectItem>
                            <SelectItem value="6 Stunden">6 Stunden</SelectItem>
                            <SelectItem value="8 Stunden">8 Stunden</SelectItem>
                          </SelectContent>
                        </Select>
                        {fieldErrors.duration && <p className="text-xs text-destructive">{fieldErrors.duration.message}</p>}
                      </div>

                      {/* Step 2 Navigation */}
                      <div className="flex items-center justify-between pt-4 border-t border-[#D9E1E2] gap-3 flex-wrap sm:flex-nowrap">
                        <Button type="button" variant="outline" onClick={handleBack} className="border-[#D9E1E2] text-[#324A6D] hover:bg-[#E8F2F1]/50 rounded-xl px-6 py-2.5 text-sm font-medium w-full sm:w-auto order-2 sm:order-1">
                          Zurück
                        </Button>
                        <Button type="button" onClick={handleNext} className="bg-[#D99000] text-white hover:bg-[#D99000]/90 rounded-xl px-7 py-2.5 text-sm font-medium w-full sm:w-auto order-1 sm:order-2 ml-auto">
                          Weiter
                        </Button>
                      </div>
                    </div>

                    {/* STEP 3: Trip Details */}
                    <div className={currentStep === 3 ? "space-y-6" : "hidden"}>
                      <div className="space-y-1.5">
                        <Label htmlFor="destinations" className="text-sm font-medium text-foreground">
                          Reiseziele oder Zwischenstopps <span className="text-destructive">*</span>
                        </Label>
                        <Textarea id="destinations" rows={3} placeholder="z. B. Marina Boulevard, El Dahar Altstadt, Hurghada Grand Aquarium" value={destinations} onChange={e => setDestinations(e.target.value)} className={`bg-background ${fieldErrors.destinations ? "border-destructive focus-visible:ring-destructive" : ""}`} required />
                        {fieldErrors.destinations && <p className="text-xs text-destructive">{fieldErrors.destinations.message}</p>}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label htmlFor="passengers" className="text-sm font-medium text-foreground">
                            Anzahl der Passagiere <span className="text-destructive">*</span>
                          </Label>
                          <Input id="passengers" type="number" min="1" max="50" placeholder="1" value={passengers} onChange={e => setPassengers(e.target.value)} className={`bg-background ${fieldErrors.passengers ? "border-destructive focus-visible:ring-destructive" : ""}`} required />
                          {fieldErrors.passengers && <p className="text-xs text-destructive">{fieldErrors.passengers.message}</p>}
                        </div>

                        <div className="space-y-1.5">
                          <Label htmlFor="suitcases" className="text-sm font-medium text-foreground">
                            Anzahl der Koffer <span className="text-destructive">*</span>
                          </Label>
                          <Input id="suitcases" type="number" min="0" max="50" placeholder="0" value={suitcases} onChange={e => setSuitcases(e.target.value)} className={`bg-background ${fieldErrors.suitcases ? "border-destructive focus-visible:ring-destructive" : ""}`} required />
                          {fieldErrors.suitcases && <p className="text-xs text-destructive">{fieldErrors.suitcases.message}</p>}
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="dropoff_location" className="text-sm font-medium text-foreground">
                          Rückkehr- oder Zielort <span className="text-destructive">*</span>
                        </Label>
                        <Input id="dropoff_location" type="text" placeholder="z. B. Rückfahrt zum Hotel oder Flughafen Hurghada" value={dropoffLocation} onChange={e => setDropoffLocation(e.target.value)} className={`bg-background ${fieldErrors.dropoffLocation ? "border-destructive focus-visible:ring-destructive" : ""}`} required />
                        {fieldErrors.dropoffLocation && <p className="text-xs text-destructive">{fieldErrors.dropoffLocation.message}</p>}
                      </div>

                      {/* Step 3 Navigation */}
                      <div className="flex items-center justify-between pt-4 border-t border-[#D9E1E2] gap-3 flex-wrap sm:flex-nowrap">
                        <Button type="button" variant="outline" onClick={handleBack} className="border-[#D9E1E2] text-[#324A6D] hover:bg-[#E8F2F1]/50 rounded-xl px-6 py-2.5 text-sm font-medium w-full sm:w-auto order-2 sm:order-1">
                          Zurück
                        </Button>
                        <Button type="button" onClick={handleNext} className="bg-[#D99000] text-white hover:bg-[#D99000]/90 rounded-xl px-7 py-2.5 text-sm font-medium w-full sm:w-auto order-1 sm:order-2 ml-auto">
                          Weiter
                        </Button>
                      </div>
                    </div>

                    {/* STEP 4: Final Details & Summary */}
                    <div className={currentStep === 4 ? "space-y-6" : "hidden"}>
                      <div className="space-y-1.5">
                        <Label htmlFor="additional_message" className="text-sm font-medium text-foreground">
                          Zusätzliche Nachricht <span className="text-xs text-muted-foreground">(Optional)</span>
                        </Label>
                        <Textarea id="additional_message" rows={3} placeholder="Kindersitze erforderlich, besondere Wünsche zur Reiseroute oder Fluginformationen..." value={additionalMessage} onChange={e => setAdditionalMessage(e.target.value)} className="bg-background" />
                      </div>

                      {/* Read-Only Booking Summary */}
                      <div className="bg-[#E8F2F1]/40 rounded-xl p-4 sm:p-5 border border-[#D9E1E2] text-sm space-y-3">
                        <div className="flex items-center justify-between pb-2 border-b border-[#D9E1E2]">
                          <h4 className="font-semibold text-[#324A6D]">Buchungsübersicht</h4>
                          <span className="text-xs text-muted-foreground">Prüfen Sie Ihre Reiseroute</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2.5 gap-x-4 text-xs sm:text-sm">
                          <div>
                            <span className="text-muted-foreground block text-xs">Vollständiger Name</span>
                            <span className="font-medium text-foreground">{fullName || "—"}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground block text-xs">WhatsApp / Telefon</span>
                            <span className="font-medium text-foreground">{phone || "—"}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground block text-xs">E-Mail</span>
                            <span className="font-medium text-foreground">{email || "—"}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground block text-xs">Wunschdatum</span>
                            <span className="font-medium text-foreground">{preferredDate || "—"}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground block text-xs">Abholort</span>
                            <span className="font-medium text-foreground">{pickupLocation || "—"}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground block text-xs">Dauer</span>
                            <span className="font-medium text-foreground">{duration || "—"}</span>
                          </div>
                          <div className="sm:col-span-2">
                            <span className="text-muted-foreground block text-xs">Ziele / Stopps</span>
                            <span className="font-medium text-foreground">{destinations || "—"}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground block text-xs">Passagiere</span>
                            <span className="font-medium text-foreground">{passengers || "—"}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground block text-xs">Koffer</span>
                            <span className="font-medium text-foreground">{suitcases || "0"}</span>
                          </div>
                          <div className="sm:col-span-2">
                            <span className="text-muted-foreground block text-xs">Zielort</span>
                            <span className="font-medium text-foreground">{dropoffLocation || "—"}</span>
                          </div>
                          {additionalMessage && <div className="sm:col-span-2">
                              <span className="text-muted-foreground block text-xs">Zusätzliche Nachricht</span>
                              <span className="font-medium text-foreground">{additionalMessage}</span>
                            </div>}
                        </div>
                      </div>

                      {/* Step 4 Navigation with Submit CTA */}
                      <div className="flex items-center justify-between pt-4 border-t border-[#D9E1E2] gap-3 flex-wrap sm:flex-nowrap">
                        <Button type="button" variant="outline" onClick={handleBack} disabled={isSubmitting} className="border-[#D9E1E2] text-[#324A6D] hover:bg-[#E8F2F1]/50 rounded-xl px-6 py-2.5 text-sm font-medium w-full sm:w-auto order-2 sm:order-1">
                          Zurück
                        </Button>

                        <Button type="submit" disabled={isSubmitting} className="bg-[#D99000] text-white hover:bg-[#D99000]/90 rounded-2xl py-3 px-8 text-base font-medium shadow-sm transition-colors flex items-center justify-center gap-2 h-auto w-full sm:w-auto order-1 sm:order-2 ml-auto">
                          {isSubmitting ? <>
                              <Loader2 className="size-5 animate-spin" />
                              <span>Anfrage wird gesendet...</span>
                            </> : <span>Buchungsanfrage senden</span>}
                        </Button>
                      </div>
                    </div>
                  </form>}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}